/**
 * Settings (DEC-198, UI_SDD §R.14), reached from the user menu at the bottom of the
 * sidebar the way the Claude app does it. One page with a section list on its left:
 *
 * - Your account (everyone): Profile, Appearance.
 * - Workspace (admins, §R.8 / DEC-086): General (API key, GitHub), Members, Usage,
 *   Scan rules (paused scans, business inputs, language support).
 *
 * Each section is its own URL (`/settings/:group/:section`). Before DEC-198 this was
 * one admin-only page at `/settings` holding every card; that URL now redirects (App.tsx).
 *
 * Distinct from AdminPage.tsx (`/scans/:id/admin`), a read-only view of one scan's
 * settings (DEC-061).
 */
import { useQuery } from '@tanstack/react-query'
import { Link, Navigate, useLocation, useParams } from 'react-router-dom'
import { settingsApi } from '../api/settings'
import { useAuth } from '../hooks/useAuth'
import { AppShell } from '../components/layout/AppShell'
import { AppearanceSection, ProfileSection } from '../components/settings/AccountSections'
import { GeneralSection } from '../components/settings/GeneralSection'
import { ImpactInputsCard } from '../components/settings/ImpactInputsCard'
import { LanguageSupportCard } from '../components/settings/LanguageSupportCard'
import { MembersCard } from '../components/settings/MembersCard'
import { ScanArchiveCard } from '../components/settings/ScanArchiveCard'
import { UsageSection } from '../components/settings/UsageSection'
import {
  GROUP_LABEL,
  SETTINGS_SECTIONS,
  defaultSettingsPath,
  findSection,
  settingsPath,
  type SettingsGroup,
} from '../components/settings/settingsSections'
import { Spinner } from '../components/shared/Spinner'
import { PageTitle } from '../components/shared/PageTitle'

const INTRO: Record<string, string> = {
  profile: 'How you appear in this workspace.',
  appearance: 'How TDIA looks on this device.',
  general: 'The shared AI key and the GitHub connection.',
  members: 'People in this workspace and the invitations still waiting.',
  usage: 'What this workspace spends on AI, and who it was for.',
  'scan-rules': 'How scans behave for the whole workspace, and the business inputs TDIA will not estimate for you.',
}

/** Workspace sections that need the tenant settings row before they can render. */
function WorkspaceSection({ id }: { id: string }) {
  const { data: settings, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: () => settingsApi.get(),
    enabled: id === 'general' || id === 'scan-rules',
  })

  if (id === 'members') return <MembersCard />
  if (id === 'usage') return <UsageSection />
  if (isLoading || !settings) {
    return (
      <div className="py-16 flex justify-center">
        <Spinner />
      </div>
    )
  }
  if (id === 'general') return <GeneralSection settings={settings} />
  return (
    <>
      {/* Paused scans (DEC-192): the waiting time before Archive and Delete permanently. */}
      <ScanArchiveCard days={settings.archive_paused_after_days} />
      {/* Business impact inputs (SDD §35.7, DEC-117): workspace configuration, kept out
          of the chart that consumes them. */}
      <ImpactInputsCard />
      {/* Language support policy (§4.12, DEC-147): a standing declaration. */}
      <LanguageSupportCard />
    </>
  )
}

/** `/settings` and `/settings/:group` land on the person's default section, query kept. */
export function SettingsRedirect() {
  const { claims } = useAuth()
  const location = useLocation()
  const profile = claims?.profile ?? null
  const isAdmin = profile === null || profile === 'admin'
  return <Navigate to={`${defaultSettingsPath(isAdmin)}${location.search}`} replace />
}

export default function SettingsPage() {
  const { claims } = useAuth()
  const { group, section } = useParams()
  const location = useLocation()
  const profile = claims?.profile ?? null
  const isAdmin = profile === null || profile === 'admin'

  const current = findSection(group, section)
  // An unknown section, or a workspace section for someone who is not an admin, lands on
  // the default for this person, keeping any query (the GitHub return carries one).
  if (!current || (current.group === 'workspace' && !isAdmin)) {
    return <Navigate to={`${defaultSettingsPath(isAdmin)}${location.search}`} replace />
  }

  const groups: SettingsGroup[] = isAdmin ? ['account', 'workspace'] : ['account']
  const isWorkspace = current.group === 'workspace'
  const title = isWorkspace ? 'Workspace settings' : 'Settings'

  return (
    <AppShell crumb={`${title} / ${current.label}`} active="settings">
      <div className="px-8 py-6">
        <PageTitle eyebrow={isWorkspace ? claims?.tenant_name || 'Workspace' : 'Your account'} className="mb-6">
          {title}
        </PageTitle>

        <div className="flex flex-col md:flex-row gap-6 md:gap-8">
          <nav aria-label="Settings sections" className="md:w-[180px] flex-none" data-testid="settings-nav">
            {groups.map((g) => (
              <div key={g} className="mb-4">
                <div className="font-semibold text-[10px] tracking-[2px] uppercase text-muted px-3 pb-2">
                  {GROUP_LABEL[g]}
                </div>
                <ul className="m-0 p-0 list-none flex md:flex-col flex-wrap gap-1">
                  {SETTINGS_SECTIONS.filter((s) => s.group === g).map((s) => {
                    const on = s.group === current.group && s.id === current.id
                    return (
                      <li key={s.id}>
                        <Link
                          to={settingsPath(s.group, s.id)}
                          aria-current={on ? 'page' : undefined}
                          className={`block px-3 py-1.5 rounded-md text-[13px] font-medium transition-colors ${
                            on ? 'bg-surface-active text-ink' : 'text-muted hover:text-body hover:bg-fill-subtle'
                          }`}
                        >
                          {s.label}
                        </Link>
                      </li>
                    )
                  })}
                </ul>
              </div>
            ))}
          </nav>

          <section aria-labelledby="settings-section-title" className="flex-1 min-w-0 max-w-[720px]">
            <h2 id="settings-section-title" className="text-[18px] font-semibold text-ink m-0">
              {current.label}
            </h2>
            <p className="text-[13px] text-muted mt-1 mb-5">{INTRO[current.id]}</p>
            {current.id === 'profile' && <ProfileSection />}
            {current.id === 'appearance' && <AppearanceSection />}
            {isWorkspace && <WorkspaceSection id={current.id} />}
          </section>
        </div>
      </div>
    </AppShell>
  )
}
